import {
    generationStarted,
    generationLogReceived,
    generationHierarchicalReceived,
    generationExecutionFlowReceived,
    generationScreenshotReceived,
    generationTestStepReceived,
    generationTokenUsageReceived,
    generationCompleted,
    generationFailed,
    generationStopped,
    replayStarted,
    replayStepStarted,
    replayStepCompleted,
    replayCompleted,
    replayFailed,
    replayStopped,
    replayLogReceived,
} from './automationStudioNewSlice';
import { RUN_STATUS } from './constants';
import { buildInstructionsFromTestPlan } from './planTransformUtils';
import {
    normalizeReplayStepStart,
    normalizeReplayStepComplete,
} from '../../utils/automation/replayWsPayloadNormalize';

function getEventType(event) {
    if (!event) return null;
    if (typeof event === 'string') return event;
    return event.type ?? event.event ?? event.name ?? null;
}

function getEventPayload(event) {
    if (!event) return {};
    if (event.payload != null) return event.payload;
    if (event.data != null) return event.data;
    return event;
}

function getRunCorrelationId(event, payload) {
    return payload?.runId ?? payload?.uniqueId ?? event?.runId ?? event?.uniqueId ?? null;
}

function getRunId(event, payload) {
    return getRunCorrelationId(event, payload) ?? payload?.sessionId ?? event?.sessionId ?? null;
}

function isStaleGenerationRun(state, runId) {
    const current = state.automationStudioNew?.generationRun;
    if (!current || current.status !== RUN_STATUS.RUNNING) return false;
    if (!runId || !current.runId) return false;
    return runId !== current.runId;
}

function isStaleReplayRun(state, runId) {
    const current = state.automationStudioNew?.replayRun;
    if (!current) return false;
    if (!runId || !current.runId) return false;
    if (current.status === RUN_STATUS.RUNNING) {
        return runId !== current.runId;
    }
    if ([RUN_STATUS.STOPPED, RUN_STATUS.COMPLETED, RUN_STATUS.FAILED].includes(current.status)) {
        return runId === current.runId;
    }
    return false;
}

function normalizeScreenshot(payload) {
    if (!payload) return null;
    return {
        ...payload,
        url: payload.url ?? payload.screenshotUrl ?? null,
        path: payload.path ?? payload.screenshotPath ?? null,
    };
}

function normalizeExecutionFlow(payload) {
    if (!payload) return null;
    if (payload.executions) return payload;
    if (Array.isArray(payload)) return { executions: payload };
    return payload;
}

export function handleGenerationWebSocketEvent(dispatch, getState, rawEvent) {
    const type = getEventType(rawEvent);
    const payload = getEventPayload(rawEvent);
    const runId = getRunCorrelationId(rawEvent, payload);
    const state = getState();

    if (type !== 'executeTest' && isStaleGenerationRun(state, runId)) {
        return { handled: false, reason: 'stale_run' };
    }

    switch (type) {
        case 'executeTest':
        case 'generationStarted':
            dispatch(generationStarted({ runId }));
            return { handled: true };

        case 'log':
            dispatch(generationLogReceived(payload?.message ?? payload));
            return { handled: true };

        case 'hierarchicalSteps':
            dispatch(generationHierarchicalReceived(payload));
            return { handled: true };

        case 'executionFlow':
            dispatch(generationExecutionFlowReceived(normalizeExecutionFlow(payload)));
            return { handled: true };

        case 'screenshot':
            dispatch(generationScreenshotReceived(normalizeScreenshot(payload)));
            return { handled: true };

        case 'testStep': {
            const raw = payload?.step ?? payload?.testStep ?? payload;
            dispatch(generationTestStepReceived(raw));
            return { handled: true };
        }

        case 'tokenUsage':
            dispatch(generationTokenUsageReceived(payload));
            return { handled: true };

        case 'testComplete': {
            const genStatus = state.automationStudioNew?.generationRun?.status;
            const canComplete = [RUN_STATUS.RUNNING, RUN_STATUS.STOPPED, RUN_STATUS.FAILED].includes(
                genStatus
            );
            if (!canComplete) {
                return { handled: false, reason: 'run_not_active' };
            }
            const instructions = buildInstructionsFromTestPlan(
                payload?.testPlan,
                state.automationStudioNew?.planDraft?.instructions ?? []
            );
            dispatch(
                generationCompleted({
                    instructions,
                    dynamicResult: payload?.dynamicResult ?? null,
                    executionRecordPlan: payload?.testPlan ?? null,
                    result: payload,
                })
            );
            return { handled: true };
        }

        case 'error':
            dispatch(generationFailed(payload?.message ?? payload?.error ?? payload ?? 'Generation error'));
            return { handled: true };

        case 'generationStopped':
        case 'stopAutomation':
            dispatch(generationStopped());
            return { handled: true };

        default:
            return { handled: false, reason: 'unknown_type' };
    }
}

export function handleReplayWebSocketEvent(dispatch, getState, rawEvent) {
    const type = getEventType(rawEvent);
    const payload = getEventPayload(rawEvent);
    const runId = getRunId(rawEvent, payload);
    const state = getState();

    if (isStaleReplayRun(state, runId) && !['replayTestPlanJson', 'replayStatus'].includes(type)) {
        return { handled: false, reason: 'stale_run' };
    }

    switch (type) {
        case 'replayTestPlanJson':
        case 'replayStarted':
            if (state.automationStudioNew?.replayRun?.status !== RUN_STATUS.RUNNING) {
                dispatch(replayStarted({ runId }));
            }
            return { handled: true };

        case 'replayStatus': {
            const status = String(payload?.status ?? payload ?? '').toLowerCase();
            if (status === 'loading' || status === 'executing') {
                if (state.automationStudioNew?.replayRun?.status !== RUN_STATUS.RUNNING) {
                    dispatch(replayStarted({ runId }));
                }
                const normalized = normalizeReplayStepStart(payload);
                if (Number.isFinite(normalized?.stepIndex)) {
                    dispatch(
                        replayStepStarted({
                            ...normalized,
                            status: 'executing',
                            sessionId: payload?.sessionId ?? normalized?.sessionId,
                            runId,
                        })
                    );
                }
            }
            return { handled: true };
        }

        case 'replayStepStart': {
            const normalized = normalizeReplayStepStart(payload);
            dispatch(
                replayStepStarted({
                    ...normalized,
                    sessionId: payload?.sessionId ?? normalized?.sessionId,
                    runId,
                })
            );
            return { handled: true };
        }

        case 'replayStepComplete': {
            const normalized = normalizeReplayStepComplete(payload);
            dispatch(
                replayStepCompleted({
                    ...normalized,
                    sessionId: payload?.sessionId ?? normalized?.sessionId,
                    runId,
                })
            );
            return { handled: true };
        }

        case 'replayLog':
            dispatch(replayLogReceived(payload));
            return { handled: true };

        case 'replayComplete':
            dispatch(replayCompleted(payload));
            return { handled: true };

        case 'replayError':
            dispatch(replayFailed(payload));
            return { handled: true };

        case 'stopReplay':
        case 'replayStopped':
            dispatch(replayStopped());
            return { handled: true };

        default:
            return { handled: false, reason: 'unknown_type' };
    }
}

export function handleAutomationStudioWebSocketEvent(dispatch, getState, rawEvent) {
    const type = getEventType(rawEvent);
    const replayTypes = new Set([
        'replayTestPlanJson',
        'replayStarted',
        'replayStatus',
        'replayStepStart',
        'replayStepComplete',
        'replayComplete',
        'replayError',
        'stopReplay',
        'replayStopped',
        'replayLog',
    ]);

    if (replayTypes.has(type)) {
        return handleReplayWebSocketEvent(dispatch, getState, rawEvent);
    }
    return handleGenerationWebSocketEvent(dispatch, getState, rawEvent);
}
